// The annotations a reporter draws on a screenshot: boxes, arrows,
// highlights, redactions and text, in the screenshot's own pixels. The model is plain
// data; `draw` paints it on a canvas.

export interface Point {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type Shape =
  | ({ kind: 'box' } & Rect)
  | ({ kind: 'highlight' } & Rect)
  /** Covers what is under it, opaquely. */
  | ({ kind: 'redact' } & Rect)
  | { kind: 'arrow'; from: Point; to: Point }
  | { kind: 'text'; x: number; y: number; text: string };

export type Tool = Shape['kind'];

/** Smallest drag, in pixels, that makes a shape. */
const MIN = 4;

/** The rectangle a drag from `a` to `b` spans, whichever way it went. */
export function normalize(a: Point, b: Point): Rect {
  return { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) };
}

/** The two barb ends of an arrow from `from` to `to`, each `len` long at 30° to the shaft. */
export function arrowHead(from: Point, to: Point, len: number): [Point, Point] {
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const barb = (d: number) => ({ x: to.x - len * Math.cos(angle + d), y: to.y - len * Math.sin(angle + d) });
  return [barb(Math.PI / 6), barb(-Math.PI / 6)];
}

function meaningful(s: Shape): boolean {
  switch (s.kind) {
    case 'box':
    case 'highlight':
    case 'redact':
      return s.w >= MIN && s.h >= MIN;
    case 'arrow':
      return Math.hypot(s.to.x - s.from.x, s.to.y - s.from.y) >= MIN * 2;
    case 'text':
      return s.text.trim().length > 0;
  }
}

/** The shapes drawn so far. */
export class Annotations {
  shapes: Shape[] = [];

  /** Adds `s`, unless it is too small to mean anything. */
  add(s: Shape): void {
    if (meaningful(s)) this.shapes = [...this.shapes, s];
  }

  undo(): void {
    this.shapes = this.shapes.slice(0, -1);
  }

  clear(): void {
    this.shapes = [];
  }
}

/** The annotation colour: one that reads on light and dark pages alike. */
export const INK = '#e5484d';
const HIGHLIGHT = 'rgba(255, 214, 10, 0.35)';

/** Paints `shapes` on `ctx`, scaled by `scale` (stroke widths and text grow with it). */
export function draw(ctx: CanvasRenderingContext2D, shapes: readonly Shape[], scale = 1): void {
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const s of shapes) {
    switch (s.kind) {
      case 'box':
        ctx.strokeStyle = INK;
        ctx.lineWidth = 3 * scale;
        ctx.strokeRect(s.x, s.y, s.w, s.h);
        break;
      case 'highlight':
        ctx.fillStyle = HIGHLIGHT;
        ctx.fillRect(s.x, s.y, s.w, s.h);
        break;
      case 'redact':
        ctx.fillStyle = '#111';
        ctx.fillRect(s.x, s.y, s.w, s.h);
        break;
      case 'arrow': {
        ctx.strokeStyle = INK;
        ctx.lineWidth = 3 * scale;
        ctx.beginPath();
        ctx.moveTo(s.from.x, s.from.y);
        ctx.lineTo(s.to.x, s.to.y);
        const [a, b] = arrowHead(s.from, s.to, 14 * scale);
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(s.to.x, s.to.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
        break;
      }
      case 'text': {
        const size = 16 * scale;
        ctx.font = `600 ${size}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
        const w = ctx.measureText(s.text).width;
        const pad = 4 * scale;
        ctx.fillStyle = INK;
        ctx.fillRect(s.x - pad, s.y - size - pad / 2, w + pad * 2, size + pad * 2);
        ctx.fillStyle = '#fff';
        ctx.fillText(s.text, s.x, s.y);
        break;
      }
    }
  }
  ctx.restore();
}
